import * as stylex from "@stylexjs/stylex";
import { TrajectoriesChart } from "charts/TrajectoriesChart";
import { Button } from "components/button";
import { Popup } from "components/popup";
import { useConfig } from "config";
import { format } from "d3-format";
import { motion } from "framer-motion";
import { useFetch } from "hooks/useFetch";
import { everything } from "lib/explorer-gql/generated";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import Carousel from "../components/carousel";
import { withFigureIOExplorer } from "../components/figure";
import { CarouselFooter, EmptyInterpretation, formatPerc, formatPercentagePoints, Highlight, InterpretationToken, i18nLookup, makeDisparityGroupLabels, Slide, SlideTitle, ToggleInterpretationButton } from "../components/interpretation";
import { metadata } from "../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER_SIMULATION";
import { COUNTRIES_EXCLUDED_DISPARITY_SIM, comparisonCountryControlTitle, countriesSimulationControlItems, countryControlPlaceholder, countryControlTitle, countryIdIso, disparitiesSimulationControlItems, disparityGroupsControlItems, disparitySimulationLookup, ExplorerContext, explorerAPI, GroupLabelLookup, getRegionOrCountryName, goalRule, makeUseControl, RiseDataDecoder, simulationControlsLabels, useFigureState, verticalAxisControlItems, verticalAxisControlLabel, viewControlItems, xAxisSubtitleLookup, xAxisTitleLookup, yAxisTitleLookup } from "../domain";
import * as M from "../materials";
import { E, O, pipe } from "../prelude";
import { colors, makeLineStyler } from "./TRAJECTORIES_SIMULATION_LMIC";
export * from "../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER_SIMULATION";
export const Data = RiseDataDecoder([
    "id",
    "value",
    "country",
    "year",
    "group",
    "series",
    "facet"
], {});
const COUNTRY_SIMULATIONS_LEGEND = [
    "SIMULATION_LEARNING",
    "SIMULATION_ACCESS"
];
const useControl = makeUseControl({
    "2-toggle-interpretation": {
        control: "MultiCheckbox",
        defaultValue: []
    },
    "1-country": {
        control: "SingleSelect",
        defaultValue: ""
    },
    "2-groups": {
        control: "Radio",
        defaultValue: "WEALTH"
    },
    "2-comparison-country": {
        control: "SingleSelect",
        defaultValue: ""
    },
    "1-simulations-countries": {
        control: "MultiCheckbox",
        defaultValue: []
    },
    "1-simulations-disparities": {
        control: "MultiCheckbox",
        defaultValue: []
    },
    "0-vertical-axis": {
        defaultValue: "LIT",
        control: "Radio"
    },
    "0-view": {
        defaultValue: "countries",
        control: "Radio"
    }
});
const ActivateSimulationSlide = ({ onActivate })=>{
    return <>
      <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.simulation-deactivated" defaultMessage="Activate the simulation to see the interpretation."/>
      <Button className={({
        className: "x1oqdnn1"
    }).className} rounded inverted onClick={()=>onActivate()}>
        Activate
      </Button>
    </>;
};
const InterpretationSimulationCountries = ({ dataset, getCountryName, chartActions, requestClose })=>{
    const country_ = useControl("1-country");
    const skill = useControl("0-vertical-axis");
    const countryControl = countryIdIso.wrap(country_);
    const comparisonCountry_ = useControl("2-comparison-country");
    const comparisonCountryControl = countryIdIso.wrap(comparisonCountry_);
    const simulations = useControl("1-simulations-countries");
    const [, actions] = useFigureState();
    const handleActivate = (simName)=>{
        actions.toggleMultiCheckboxItem("1-simulations-countries", simName);
    };
    const intl = useIntl();
    const { setHighlightedSeries } = chartActions;
    const findPoint = (d)=>d.year === 9;
    const age9Values = useMemo(()=>{
        return Object.fromEntries(dataset.filter(findPoint).map((d)=>[
                d.series,
                d.value || 0
            ]));
    }, [
        dataset
    ]);
    const { comparisonCountry, country, age9ComparisonValue, age9CountryValue, countryDifference, // accessDifference,
    accessChange, accessName, learningChange, learningDifference, learningChangeAdverb, learningName, skillName, skillVerb } = useMemo(()=>{
        const year9pointCurrent = dataset.find((p)=>findPoint(p) && p.series === "CURRENT");
        const year9pointComparison = dataset.find((p)=>findPoint(p) && p.series === "COMPARISON");
        const year9pointAccess = dataset.find((p)=>findPoint(p) && p.series === "SIMULATION_ACCESS");
        const year9pointLearning = dataset.find((p)=>findPoint(p) && p.series === "SIMULATION_LEARNING");
        const countryDifference = Math.round((age9Values.COMPARISON - age9Values.CURRENT) * 100) / 100;
        const learningDifference = Math.round((age9Values.SIMULATION_LEARNING - age9Values.CURRENT) * 100) / 100;
        const accessSimDifference = Math.round((age9Values.SIMULATION_ACCESS - age9Values.CURRENT) * 100) / 100;
        return {
            age9CountryValue: <InterpretationToken color={colors.current} chartActions={chartActions} highlightPoint={year9pointCurrent} highlightSeries="CURRENT">
          {formatPerc(age9Values.CURRENT)}
        </InterpretationToken>,
            age9ComparisonValue: <InterpretationToken color={colors.comparison} chartActions={chartActions} highlightPoint={year9pointComparison} highlightSeries="COMPARISON">
          {formatPerc(age9Values.COMPARISON)}
        </InterpretationToken>,
            country: <InterpretationToken chartActions={chartActions} color={colors.current} highlightSeries="CURRENT">
          {getCountryName(countryControl)}
        </InterpretationToken>,
            comparisonCountry: <InterpretationToken chartActions={chartActions} color={colors.comparison} highlightSeries="COMPARISON">
          {getCountryName(comparisonCountryControl)}
        </InterpretationToken>,
            countryDifference: <InterpretationToken chartActions={chartActions} highlightPoint={year9pointCurrent}>
          {formatPercentagePoints(Math.abs(countryDifference), intl)}
        </InterpretationToken>,
            accessDifference: <InterpretationToken chartActions={chartActions} highlightPoint={year9pointAccess} highlightSeries="SIMULATION_ACCESS" color={colors.accessSimulation}>
          {formatPercentagePoints(Math.abs(accessSimDifference), intl)}
        </InterpretationToken>,
            accessName: <InterpretationToken chartActions={chartActions} color={colors.accessSimulation} highlightSeries="SIMULATION_ACCESS">
          <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.accessName" defaultMessage="access simulation"/>
        </InterpretationToken>,
            accessChange: <InterpretationToken chartActions={chartActions} highlightPoint={year9pointAccess} highlightSeries="SIMULATION_ACCESS" color={Math.round(accessSimDifference * 100) !== 0 ? colors.accessSimulation : undefined}>
          {Math.round(accessSimDifference * 100) === 0 ? intl.formatMessage(i18nLookup.noChange) : accessSimDifference > 0 ? intl.formatMessage(i18nLookup.increase) : intl.formatMessage(i18nLookup.decrease)}
        </InterpretationToken>,
            learningName: <InterpretationToken chartActions={chartActions} color={colors.learningSimulation} highlightSeries="SIMULATION_LEARNING">
          <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.learningName" defaultMessage="learning simulation"/>
        </InterpretationToken>,
            learningDifference: <InterpretationToken chartActions={chartActions} highlightPoint={year9pointLearning} color={colors.learningSimulation} highlightSeries="SIMULATION_LEARNING">
          {formatPercentagePoints(Math.abs(learningDifference), intl)}
        </InterpretationToken>,
            learningChange: <InterpretationToken chartActions={chartActions} highlightPoint={year9pointLearning} highlightSeries="SIMULATION_LEARNING" color={Math.round(learningDifference * 100) !== 0 ? colors.learningSimulation : undefined}>
          {Math.round(learningDifference * 100) === 0 ? intl.formatMessage(i18nLookup.noChange) : learningDifference > 0 ? intl.formatMessage(i18nLookup.increase) : intl.formatMessage(i18nLookup.decrease)}
        </InterpretationToken>,
            learningChangeAdverb: <>
          {Math.round(learningDifference * 100) === 0 ? "" : learningDifference > 0 ? intl.formatMessage(i18nLookup.greater) : intl.formatMessage(i18nLookup.lower)}
        </>,
            skillName: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillName.LIT"]) : intl.formatMessage(i18nLookup["skillName.NUM"])}
        </Highlight>,
            skillVerb: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillVerb.LIT"]) : intl.formatMessage(i18nLookup["skillVerb.NUM"])}
        </Highlight>
        };
    }, [
        age9Values.COMPARISON,
        age9Values.CURRENT,
        age9Values.SIMULATION_ACCESS,
        age9Values.SIMULATION_LEARNING,
        chartActions,
        comparisonCountryControl,
        countryControl,
        dataset,
        getCountryName,
        intl,
        skill
    ]);
    if (!countryControl || !comparisonCountryControl || !age9Values.CURRENT || !age9Values.COMPARISON || dataset.length === 0) {
        return null;
    }
    const handleSlideChange = (slideNumber)=>{
        if (slideNumber === 0) {
            setHighlightedSeries("CURRENT");
            setTimeout(()=>{
                setHighlightedSeries("COMPARISON");
                setTimeout(()=>{
                    setHighlightedSeries(undefined);
                }, 1000);
            }, 1000);
        }
        if (slideNumber === 1) {
            setHighlightedSeries("SIMULATION_ACCESS");
            setTimeout(()=>setHighlightedSeries(undefined), 1000);
        } else if (slideNumber === 2) {
            setHighlightedSeries("SIMULATION_LEARNING");
            setTimeout(()=>setHighlightedSeries(undefined), 1000);
        }
    };
    return <Carousel FooterComponent={(footerProps)=><CarouselFooter {...footerProps} onClose={requestClose}/>} onSlideChange={handleSlideChange}>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.common.interpretation-title",
        defaultMessage: "Interpretation"
    }))}
        </SlideTitle>
        <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.interpretation-content" defaultMessage="The blue trajectory shows the current rate of learning in {country}. The red trajectory shows the current rate of learning in the selected comparison country, {comparisonCountry}. SDG 4.1.1 expects all children to have acquired foundational {skillName} skills by Grade 3, which typically corresponds to age 9. Currently, about {age9CountryValue} of 9-year-olds can {skillVerb} at an age-appropriate level in {country} and {age9ComparisonValue} of 9-year-olds can {skillVerb} at an age-appropriate level in {comparisonCountry}, a difference of about {countryDifference}." values={{
        country: country,
        comparisonCountry: comparisonCountry,
        skillName: skillName,
        age9CountryValue: age9CountryValue,
        skillVerb: skillVerb,
        age9ComparisonValue: age9ComparisonValue,
        countryDifference: countryDifference
    }}/>
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.access-slide-title",
        defaultMessage: "Access simulation"
    }))}
        </SlideTitle>
        {simulations.includes("SIMULATION_ACCESS") ? <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.access-content" defaultMessage="The simulations estimate how much different policy priorities might close this gap. Access-oriented policies try to increase grade attainment (the grade that children reach in school). The {accessName} estimates what might happen if every out-of-school child in {country} attained the same grade, and associated learning level, as the average child their age who is still in school. In {country}, the access simulation results in {accessChange} in the percentage of 9-year-olds who can {skillVerb} at the age-appropriate level." values={{
        accessName: accessName,
        country: country,
        accessChange: accessChange,
        skillVerb: skillVerb
    }}/> : <ActivateSimulationSlide onActivate={()=>handleActivate("SIMULATION_ACCESS")}/>}
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.learning-slide-title",
        defaultMessage: "Learning simulation"
    }))}
        </SlideTitle>
        {simulations.includes("SIMULATION_LEARNING") ? <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.learning-content" defaultMessage="Learning-oriented policies try to increase the rate of learning per grade. The {learningName} estimates what might happen if access in {country} remained unchanged, but children learned as much per grade as their peers in {comparisonCountry}. This might result in {learningChange} in the percentage of 9-year-olds who can {skillVerb} at the age-appropriate level in {country}. The learning simulation results in about {learningDifference} {learningChangeAdverb} estimated learning gains than the access simulation at age 9." values={{
        learningName: learningName,
        country: country,
        comparisonCountry: comparisonCountry,
        learningChange: learningChange,
        skillVerb: skillVerb,
        learningDifference: learningDifference,
        learningChangeAdverb: learningChangeAdverb
    }}/> : <ActivateSimulationSlide onActivate={()=>handleActivate("SIMULATION_LEARNING")}/>}
      </Slide>
    </Carousel>;
};
const InterpretationSimulationDisparities = ({ dataset, getCountryName, chartActions, requestClose })=>{
    const country_ = useControl("1-country");
    const skill = useControl("0-vertical-axis");
    const group = useControl("2-groups");
    const { setHighlightedSeries } = chartActions;
    const [, actions] = useFigureState();
    const simulations = useControl("1-simulations-disparities");
    const handleActivate = (simName)=>{
        actions.toggleMultiCheckboxItem("1-simulations-disparities", simName);
    };
    const countryControl = countryIdIso.wrap(country_);
    const comparisonGroup_ = useControl("2-groups");
    const comparisonGroupControl = countryIdIso.wrap(comparisonGroup_);
    const intl = useIntl();
    const age9Values = useMemo(()=>{
        return Object.fromEntries(dataset.filter((d)=>d.year === 9).map((d)=>[
                d.series,
                d.value || 0
            ]));
    }, [
        dataset
    ]);
    const disparityGroupLabels = useMemo(()=>makeDisparityGroupLabels(intl), [
        intl
    ]);
    const { country, age9ComparisonValue, age9CountryValue, groupDifference, groupDifferenceAdjective, groupDifferenceAdverb, accessDifference, accessChange, accessName, learningChange, learningDifference, learningName, skillVerb, skillName, groupLeft, groupRight, rightGroupMinGoalDifference } = useMemo(()=>{
        const age9pointCurrent = dataset.find((x)=>x.year === 9 && x.series === "CURRENT");
        const age9pointComparison = dataset.find((x)=>x.year === 9 && x.series === "COMPARISON");
        const age9pointAccess = dataset.find((x)=>x.year === 9 && x.series === "SIMULATION_ACCESS");
        const age9pointLearning = dataset.find((x)=>x.year === 9 && x.series === "SIMULATION_LEARNING");
        const groupDifference = Math.round((age9Values.COMPARISON - age9Values.CURRENT) * 100) / 100;
        const learningDifference = Math.round((age9Values.SIMULATION_LEARNING - age9Values.CURRENT) * 100) / 100;
        const accessSimDifference = Math.round((age9Values.SIMULATION_ACCESS - age9Values.CURRENT) * 100) / 100;
        return {
            age9CountryValue: <InterpretationToken chartActions={chartActions} color={colors.current} highlightPoint={age9pointCurrent} highlightSeries="CURRENT">
          {formatPerc(age9Values.CURRENT)}
        </InterpretationToken>,
            age9ComparisonValue: <InterpretationToken chartActions={chartActions} color={colors.comparison} highlightPoint={age9pointComparison} highlightSeries="COMPARISON">
          {formatPerc(age9Values.COMPARISON)}
        </InterpretationToken>,
            country: <Highlight>{getCountryName(countryControl)}</Highlight>,
            comparisonCountry: <InterpretationToken chartActions={chartActions} color={colors.comparison} highlightSeries="COMPARISON">
          {getCountryName(comparisonGroupControl)}
        </InterpretationToken>,
            groupDifference: <InterpretationToken chartActions={chartActions} highlightPoint={age9pointCurrent}>
          {formatPercentagePoints(Math.abs(groupDifference), intl)}
        </InterpretationToken>,
            groupDifferenceAdjective: groupDifference > 0 ? intl.formatMessage(i18nLookup.higher) : intl.formatMessage(i18nLookup.lower),
            groupDifferenceAdverb: groupDifference > 0 ? intl.formatMessage(i18nLookup.more) : intl.formatMessage(i18nLookup.less),
            accessDifference: <InterpretationToken chartActions={chartActions} color={colors.accessSimulation} highlightPoint={age9pointAccess} highlightSeries="SIMULATION_ACCESS">
          {formatPercentagePoints(Math.abs(accessSimDifference), intl)}
        </InterpretationToken>,
            accessName: <InterpretationToken chartActions={chartActions} color={colors.accessSimulation} highlightSeries="SIMULATION_ACCESS">
          <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.accessName" defaultMessage="access simulation"/>
        </InterpretationToken>,
            accessChange: <Highlight>
          {Math.floor(accessSimDifference * 100) === 0 ? intl.formatMessage(i18nLookup.noChange) : accessSimDifference > 0 ? intl.formatMessage(i18nLookup.increase) : intl.formatMessage(i18nLookup.decrease)}
        </Highlight>,
            learningName: <InterpretationToken color={colors.learningSimulation} highlightSeries="SIMULATION_LEARNING" chartActions={chartActions}>
          <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.learningName" defaultMessage="learning simulation"/>
        </InterpretationToken>,
            learningDifference: <InterpretationToken chartActions={chartActions} color={colors.learningSimulation} highlightSeries="SIMULATION_LEARNING" highlightPoint={age9pointLearning}>
          {formatPercentagePoints(Math.abs(learningDifference), intl)}
        </InterpretationToken>,
            learningChange: <Highlight>
          {learningDifference === 0 ? intl.formatMessage(i18nLookup.noChange) : learningDifference > 0 ? intl.formatMessage(i18nLookup.increase) : intl.formatMessage(i18nLookup.decrease)}
        </Highlight>,
            skillName: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillName.LIT"]) : intl.formatMessage(i18nLookup["skillName.NUM"])}
        </Highlight>,
            skillVerb: <Highlight>
          {skill === "LIT" ? intl.formatMessage(i18nLookup["skillVerb.LIT"]) : intl.formatMessage(i18nLookup["skillVerb.NUM"])}
        </Highlight>,
            groupLeft: <InterpretationToken chartActions={chartActions} highlightSeries="CURRENT" color={colors.current}>
          {disparityGroupLabels[group][0]}
        </InterpretationToken>,
            groupRight: <InterpretationToken chartActions={chartActions} highlightSeries="COMPARISON" color={colors.comparison}>
          {disparityGroupLabels[group][1]}
        </InterpretationToken>,
            rightGroupMinGoalDifference: <InterpretationToken chartActions={chartActions} highlightPoint={age9pointComparison} highlightSeries="COMPARISON" color={colors.comparison}>
          {formatPerc(1 - Math.abs(age9Values.COMPARISON))}
        </InterpretationToken>
        };
    }, [
        age9Values.COMPARISON,
        age9Values.CURRENT,
        age9Values.SIMULATION_ACCESS,
        age9Values.SIMULATION_LEARNING,
        chartActions,
        comparisonGroupControl,
        countryControl,
        dataset,
        disparityGroupLabels,
        getCountryName,
        group,
        intl,
        skill
    ]);
    const handleSlideChange = (slideNumber)=>{
        if (slideNumber === 0) {
            setHighlightedSeries("CURRENT");
            setTimeout(()=>{
                setHighlightedSeries("COMPARISON");
                setTimeout(()=>{
                    setHighlightedSeries(undefined);
                }, 1000);
            }, 1000);
        }
        if (slideNumber === 1) {
            setHighlightedSeries("SIMULATION_ACCESS");
            setTimeout(()=>setHighlightedSeries(undefined), 1000);
        } else if (slideNumber === 2) {
            setHighlightedSeries("SIMULATION_LEARNING");
            setTimeout(()=>setHighlightedSeries(undefined), 1000);
        }
    };
    if (!countryControl || !comparisonGroupControl || !age9Values.CURRENT || !age9Values.COMPARISON || dataset.length === 0) {
        return null;
    }
    return <Carousel FooterComponent={(footerProps)=><CarouselFooter {...footerProps} onClose={requestClose}/>} onSlideChange={handleSlideChange}>
      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.groups.trajectories-slide-title",
        defaultMessage: "Interpretation"
    }))}
        </SlideTitle>
        <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.groups.trajectories-content" defaultMessage="The blue trajectory shows the current rate of learning of {groupLeft} in {country}. The red trajectory shows the current rate of learning of {groupRight} in {country}. SDG 4.1.1 expects all children to have acquired foundational {skillName} skills by Grade 3, which typically corresponds to age 9. Currently, about {age9CountryValue} per cent of 9-year-old {groupLeft} and {age9ComparisonValue} per cent of 9-year-old {groupRight} can {skillVerb} at an age-appropriate level in {country}, a difference of about {groupDifference}. This difference is partly because {groupRight} have {groupDifferenceAdjective} grade attainment (they reach {groupDifferenceAdjective} grades in school) than {groupLeft}, and partly because they learn {groupDifferenceAdverb} per grade than {groupLeft}." values={{
        groupLeft: groupLeft,
        country: country,
        groupRight: groupRight,
        skillName: skillName,
        age9CountryValue: age9CountryValue,
        age9ComparisonValue: age9ComparisonValue,
        skillVerb: skillVerb,
        groupDifference: groupDifference,
        groupDifferenceAdjective: groupDifferenceAdjective,
        groupDifferenceAdverb: groupDifferenceAdverb
    }}/>
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.groups.access-slide-title",
        defaultMessage: "Access simulation"
    }))}
        </SlideTitle>
        {simulations.includes("SIMULATION_ACCESS") ? <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.groups.access-content" defaultMessage="The simulations estimate how much different equality policies might close this gap. The {accessName} estimates what might happen if {groupLeft} in {country} attained the same grade, on average, as {groupRight}, but maintained their current rate of learning per grade. The equal access simulation results in {accessChange} of {accessDifference} in the proportion of 9-year-old {groupLeft} who can {skillVerb} at the age-appropriate level in {country}." values={{
        accessName: accessName,
        groupLeft: groupLeft,
        country: country,
        groupRight: groupRight,
        accessChange: accessChange,
        accessDifference: accessDifference,
        skillVerb: skillVerb
    }}/> : <ActivateSimulationSlide onActivate={()=>handleActivate("SIMULATION_ACCESS")}/>}
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.groups.learning-slide-title",
        defaultMessage: "Learning simulation"
    }))}
        </SlideTitle>
        {simulations.includes("SIMULATION_LEARNING") ? <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.groups.learning-content" defaultMessage="The {learningName} estimates what might happen if {groupLeft} in {country} maintained their current grade attainment, but learned as much per grade as {groupRight} currently do. The simulation results in {learningChange} of {learningDifference} in the proportion of 9-year-old {groupLeft} who can {skillVerb} at the age-appropriate level in {country}." values={{
        learningName: learningName,
        groupLeft: groupLeft,
        country: country,
        groupRight: groupRight,
        learningChange: learningChange,
        learningDifference: learningDifference,
        skillVerb: skillVerb
    }}/> : <ActivateSimulationSlide onActivate={()=>handleActivate("SIMULATION_LEARNING")}/>}
      </Slide>

      <Slide>
        <SlideTitle>
          {intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.interpretation.simulation.groups.conclusion-slide-title",
        defaultMessage: "Conclusion"
    }))}
        </SlideTitle>
        <FormattedMessage id="fig.learning.trajectories.interpretation.simulation.groups.conclusion-content" defaultMessage="While focusing on inequality between {groupRight} and {groupLeft}, it is important to also analyze how far away more advantaged children are from achieving learning goals. At age 9, the relative difference in learning between {groupRight} and {groupLeft} is {groupDifference}. At the same time, {rightGroupMinGoalDifference} of {groupRight} are not meeting the minimum learning goal set by SDG 4.1.1. This is critical context for understanding the potential, and potential limits, of different equality policies." values={{
        groupRight: groupRight,
        groupLeft: groupLeft,
        groupDifference: groupDifference,
        rightGroupMinGoalDifference: rightGroupMinGoalDifference
    }}/>
      </Slide>
    </Carousel>;
};
export const Chart = ({ renderMode })=>{
    const [chartData, setChartData] = useState([]);
    const ctx = React.useContext(ExplorerContext);
    const [state, mutate] = ctx.immer;
    const [, actions] = useFigureState();
    const intl = useIntl();
    const env = useConfig();
    // Controls selections
    const interpretation = useControl("2-toggle-interpretation");
    const interpretationToggled = interpretation.length > 0;
    const view = useControl("0-view");
    const country = useControl("1-country");
    const group = useControl("2-groups");
    const comparisonCountry = useControl("2-comparison-country");
    const skill = useControl("0-vertical-axis");
    const countriesSimulations = useControl("1-simulations-countries");
    const disparitiesSimulations = useControl("1-simulations-disparities");
    const selectedView = useControl("0-view");
    const toggleInterpretationRef = useRef(null);
    const chartContainerRef = useRef(null);
    const getCountryName = useCallback((country)=>getRegionOrCountryName({
            input: {
                country: country
            },
            regions: env.regions,
            countries: env.countries
        }), [
        env
    ]);
    const getCountryInterpretationName = useCallback((country)=>{
        return country === "lmic" ? intl.formatMessage(i18nLookup.averageLowLowerCountries) : getCountryName(country);
    }, [
        getCountryName,
        intl
    ]);
    useEffect(()=>{
        setChartData([]);
    }, [
        selectedView
    ]);
    useEffect(()=>{
        if (selectedView === "countries") {
            actions.setTitle(intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.simulations.countries.title",
                defaultMessage: "Policy simulations using learning trajectories: comparing {country1} and {country2}"
            }), {
                country1: getCountryName(countryIdIso.wrap(country)),
                country2: getCountryName(countryIdIso.wrap(comparisonCountry))
            }));
        } else {
            actions.setTitle(intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.simulations.disparities.title",
                defaultMessage: "Policy simulations using learning trajectories: comparing groups inside {countryName}"
            }), {
                countryName: getCountryName(countryIdIso.wrap(country))
            }));
        }
    }, [
        actions,
        intl,
        getCountryName,
        country,
        comparisonCountry,
        selectedView
    ]);
    // Fetch data based on current controls selection
    useFetch({
        pause: false,
        fetch: useCallback(async ()=>{
            const shouldReturnEarly = selectedView === "countries" && !(comparisonCountry && country) || comparisonCountry === country || selectedView === "disparities" && !country;
            if (shouldReturnEarly) {
                return [];
            }
            const simulations = await fetchSimulations(country, comparisonCountry, skill, group, selectedView);
            return parseSimulations(simulations);
        }, [
            comparisonCountry,
            country,
            group,
            selectedView,
            skill
        ]),
        onFetch: ()=>{
            mutate((draft)=>{
                draft.status = "fetching";
            });
        },
        onSuccess: (data)=>{
            mutate((draft)=>{
                draft.status = "data";
                setChartData(data);
            });
        },
        onError: (e)=>{
            mutate((draft)=>{
                draft.status = "error";
                draft.errorMessage = e instanceof Error ? e.message : `${e}`;
            });
        }
    });
    useEffect(()=>{
        actions.setSelectionControl("2-toggle-interpretation", {
            type: "MultiCheckbox",
            label: "",
            position: "TOOLBAR",
            selected: O.none
        }, [
            {
                value: "toggled",
                Component: (props)=><ToggleInterpretationButton animation={view === "countries" && country !== "" && comparisonCountry !== "" || view === "disparities" && country !== ""} ref={toggleInterpretationRef} {...props}/>,
                label: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.interpretation.toggle",
                    defaultMessage: "Interpretation"
                }))
            }
        ]);
    }, [
        view,
        country,
        comparisonCountry,
        actions,
        intl
    ]);
    // Set controls
    useEffect(()=>{
        actions.setSelectionControl("0-vertical-axis", {
            type: "Radio",
            title: intl.formatMessage(verticalAxisControlLabel),
            selected: O.some("LIT"),
            position: "RIGHT"
        }, verticalAxisControlItems(intl));
        actions.setSelectionControl("0-view", {
            type: "Radio",
            title: intl.formatMessage(defineMessage({
                id: "fig.learning.trajectories.explorer.controls.simulations.view.title",
                defaultMessage: "Policy simulation for"
            })),
            selected: O.some("countries"),
            position: "LEFT"
        }, viewControlItems(intl));
        actions.setSelectionControl("1-country", {
            type: "SingleSelect",
            title: intl.formatMessage(countryControlTitle),
            label: intl.formatMessage(countryControlPlaceholder),
            selected: renderMode === "dropzone" ? O.some("BLR") : O.none,
            position: "LEFT"
        }, state.controls.countries.filter((country)=>!(selectedView === "disparities" && COUNTRIES_EXCLUDED_DISPARITY_SIM.includes(country.value))));
        if (selectedView === "countries") {
            actions.clearControl("2-groups");
            actions.clearControl("1-simulations-disparities");
            actions.setSelectionControl("1-simulations-countries", {
                type: "MultiCheckbox",
                selected: O.some([
                    "SIMULATION_ACCESS",
                    "SIMULATION_LEARNING"
                ]),
                position: "RIGHT",
                ...simulationControlsLabels(intl)
            }, countriesSimulationControlItems(intl));
            actions.setSelectionControl("2-comparison-country", {
                type: "SingleSelect",
                title: intl.formatMessage(comparisonCountryControlTitle),
                selected: renderMode === "dropzone" ? O.some("STP") : O.none,
                label: intl.formatMessage(countryControlPlaceholder),
                position: "LEFT"
            }, state.controls.countries.filter((d)=>d.value !== country));
        } else {
            actions.clearControl("2-comparison-country");
            actions.clearControl("1-simulations-countries");
            actions.setSelectionControl("1-simulations-disparities", {
                type: "MultiCheckbox",
                selected: O.some([
                    "SIMULATION_ACCESS",
                    "SIMULATION_LEARNING"
                ]),
                position: "RIGHT",
                ...simulationControlsLabels(intl)
            }, disparitiesSimulationControlItems(intl));
            actions.setSelectionControl("2-groups", {
                type: "Radio",
                title: intl.formatMessage(defineMessage({
                    id: "fig.learning.trajectories.explorer.simulations.controls.groups.title",
                    defaultMessage: "Groups"
                })),
                selected: O.some("WEALTH"),
                position: "LEFT"
            }, disparityGroupsControlItems(intl));
        }
    }, [
        actions,
        state.controls.countries,
        selectedView,
        country,
        intl,
        comparisonCountry,
        renderMode
    ]);
    const dataForView = useMemo(()=>chartData.filter((d)=>{
            const selectedSimulations = selectedView === "countries" ? countriesSimulations : disparitiesSimulations;
            return [
                "CURRENT",
                "COMPARISON",
                ...selectedSimulations
            ].includes(d.series);
        }), [
        selectedView,
        countriesSimulations,
        disparitiesSimulations,
        chartData
    ]);
    const currentSeries = selectedView === "countries" ? getCountryName(countryIdIso.wrap(country)) : intl.formatMessage(GroupLabelLookup[disparitySimulationLookup[group].group]);
    const comparisonSeries = selectedView === "countries" ? getCountryName(countryIdIso.wrap(comparisonCountry)) : intl.formatMessage(GroupLabelLookup[disparitySimulationLookup[group].comparisonGroup]);
    const seriesAccessor = useCallback((d)=>d.series, []);
    const [highlightedSeries, setHighlightedSeries] = useState();
    const [lockActivePoint, setLockActivePoint] = useState();
    const lineStyler = useMemo(()=>makeLineStyler(highlightedSeries), [
        highlightedSeries
    ]);
    const chartActions = useMemo(()=>{
        return {
            setHighlightedSeries,
            setLockActivePoint
        };
    }, [
        setHighlightedSeries,
        setLockActivePoint
    ]);
    const handleToggleInterpretation = ()=>{
        actions.updateSelectionControl("2-toggle-interpretation", O.some(interpretationToggled ? [] : [
            "toggled"
        ]));
    };
    return <>
      <motion.div ref={chartContainerRef} style={{
        position: "relative"
    }} initial={false} animate={{
        opacity: state.status === "fetching" ? 0.25 : 1
    }} transition={{
        duration: 1
    }}>
        <Popup open={interpretationToggled} container={chartContainerRef.current} anchor={toggleInterpretationRef.current} anchorPosition={{
        horizontal: "end",
        vertical: "start"
    }} anchorOffset={{
        horizontal: 20,
        vertical: -15
    }} onClose={handleToggleInterpretation} style={{
        width: 275
    }}>
          {view === "countries" ? <>
              {country === "" || comparisonCountry === "" ? <EmptyInterpretation>
                  <SlideTitle>
                    <FormattedMessage id="fig.learning.trajectories.explorer.empty-title" defaultMessage="Interpretation"/>
                  </SlideTitle>
                  <FormattedMessage id="fig.learning.trajectories.explorer.empty-select-countries" defaultMessage="Data interpretation is not available until filters are selected. Select countries to get started."/>
                </EmptyInterpretation> : <InterpretationSimulationCountries dataset={dataForView} getCountryName={getCountryInterpretationName} chartActions={chartActions} requestClose={()=>handleToggleInterpretation()}/>}
            </> : view === "disparities" ? <>
              {country === "" ? <EmptyInterpretation>
                  <SlideTitle>
                    <FormattedMessage id="fig.learning.trajectories.explorer.empty-title" defaultMessage="Interpretation"/>
                  </SlideTitle>
                  <FormattedMessage id="fig.learning.trajectories.explorer.empty-select-country" defaultMessage="Data interpretation is not available until filters are selected. Select a country to get started."/>
                </EmptyInterpretation> : <InterpretationSimulationDisparities dataset={dataForView} getCountryName={getCountryInterpretationName} chartActions={chartActions} requestClose={()=>handleToggleInterpretation()}/>}
            </> : null}
        </Popup>
        <TrajectoriesChart emptyChartMessage={view === "disparities" ? intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.explorer.empty-country",
        defaultMessage: "Choose a country to get started"
    })) : intl.formatMessage(defineMessage({
        id: "fig.learning.trajectories.explorer.empty-countries",
        defaultMessage: "Choose a country and a comparison country to get started"
    }))} height={M.chartHeight.m} dataset={dataForView} seriesAccessor={seriesAccessor} xAccessor={(d)=>d.year} yAccessor={(d)=>d.value} yAxisTitle={intl.formatMessage(yAxisTitleLookup[skill])} xAxisTitle={intl.formatMessage(xAxisTitleLookup["AGE"])} xAxisSubtitle={xAxisSubtitleLookup["AGE"] ? intl.formatMessage(xAxisSubtitleLookup["AGE"]) : undefined} formatTooltipLabel={(d)=>{
        const x = seriesLabelsLookup(currentSeries, comparisonSeries)[d.series];
        return typeof x === "string" ? x : intl.formatMessage(x);
    }} formatTooltipValue={(d)=>d.value === null ? intl.formatMessage(defineMessage({
            id: "fig.label.data.not.available",
            defaultMessage: "Not Available"
        })) : format(".0%")(d.value)} rules={chartData.length > 0 ? [
        goalRule(intl, "AGE", skill, true)
    ] : []} lineStyle={lineStyler} backgroundSeriesFilter={(series)=>highlightedSeries ? series !== highlightedSeries : false} showColorLegend={true} formatLegendLabel={(series)=>legendLookup(currentSeries, comparisonSeries)[selectedView][series]} legendValues={[
        "COMPARISON",
        ...COUNTRY_SIMULATIONS_LEGEND.filter((simulation)=>countriesSimulations.includes(simulation) || disparitiesSimulations.includes(simulation)),
        "CURRENT"
    ]} flipTooltip={true} lockActivePoint={lockActivePoint}/>
      </motion.div>
    </>;
};
export default withFigureIOExplorer({
    metadata,
    Chart,
    size: "main",
    csv: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_trajectories_fig_TRAJECTORIES_EXPLORER.xlsx", import.meta.url).href
});
const legendLookup = (currentSeries, comparisonSeries)=>({
        countries: {
            SIMULATION_ACCESS: <FormattedMessage id="Access simulation (100% grade attainment in {currentSeries} * current learning per grade)" defaultMessage="Access simulation (100% grade attainment in {currentSeries} * current learning per grade)" values={{
                currentSeries: currentSeries
            }}/>,
            SIMULATION_LEARNING: <FormattedMessage id="Learning simulation (grade attainment in {currentSeries} * learning per grade in {comparisonSeries})" defaultMessage="Learning simulation (grade attainment in {currentSeries} * learning per grade in {comparisonSeries})" values={{
                currentSeries: currentSeries,
                comparisonSeries: comparisonSeries
            }}/>,
            CURRENT: <FormattedMessage id="Current learning in {currentSeries}" defaultMessage="Current learning in {currentSeries}" values={{
                currentSeries: currentSeries
            }}/>,
            COMPARISON: <FormattedMessage id="Current learning in {comparisonSeries}" defaultMessage="Current learning in {comparisonSeries}" values={{
                comparisonSeries: comparisonSeries
            }}/>
        },
        disparities: {
            SIMULATION_ACCESS: <FormattedMessage id="Equal access simulation (if {0} got as much schooling as {1})" defaultMessage="Equal access simulation (if {0} got as much schooling as {1})" values={{
                "0": currentSeries.toLowerCase(),
                "1": comparisonSeries.toLowerCase()
            }}/>,
            SIMULATION_LEARNING: <FormattedMessage id="Equal learning simulation (if {0} learned as much per grade as {1})" defaultMessage="Equal learning simulation (if {0} learned as much per grade as {1})" values={{
                "0": currentSeries.toLowerCase(),
                "1": comparisonSeries.toLowerCase()
            }}/>,
            CURRENT: <FormattedMessage id="Current learning of {0}" defaultMessage="Current learning of {0}" values={{
                "0": currentSeries.toLowerCase()
            }}/>,
            COMPARISON: <FormattedMessage id="Current learning of {0}" defaultMessage="Current learning of {0}" values={{
                "0": comparisonSeries.toLowerCase()
            }}/>
        }
    });
const seriesLabelsLookup = (currentSeries, comparisonSeries)=>({
        SIMULATION_ACCESS: defineMessage({
            id: "fig.learning.trajectories.explorer.tooltip.label.simulationAccess",
            defaultMessage: "Access simulation"
        }),
        SIMULATION_LEARNING: defineMessage({
            id: "fig.learning.trajectories.explorer.tooltip.label.simulationLearning",
            defaultMessage: "Learning simulation"
        }),
        CURRENT: currentSeries,
        COMPARISON: comparisonSeries
    });
const parseSimulations = (rawSimulations)=>{
    return pipe(Data.decode(rawSimulations), E.fold((e)=>{
        console.warn("Error while fetching simulations", e);
        throw new Error(`Invalid data ${e instanceof Error ? e.message : e}`);
    }, (x)=>x));
};
const fetchSimulations = async (selectedCountry, selectedComparisonCountry, selectedSkill, selectedGroup, view)=>{
    if (view === "countries") {
        const { getCountrySimulations } = await explorerAPI.query({
            getCountrySimulations: [
                {
                    country: selectedCountry,
                    comparisonCountry: selectedComparisonCountry,
                    skill: selectedSkill
                },
                everything
            ]
        });
        return getCountrySimulations;
    }
    const { getEqualitySimulations } = await explorerAPI.query({
        getEqualitySimulations: [
            {
                country: selectedCountry,
                group: disparitySimulationLookup[selectedGroup].group,
                comparisonGroup: disparitySimulationLookup[selectedGroup].comparisonGroup,
                skill: selectedSkill
            },
            everything
        ]
    });
    return getEqualitySimulations;
};
